import React, { useState, useEffect, useCallback } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import GetAppIcon from '@mui/icons-material/GetApp';
import { supabase } from '../utils/supabaseClient';
import { fetchBulkExportData, generateBulkCSV, downloadFile } from '../utils/bulkExportUtils';

const BulkExportDialog = ({ open, onClose }) => {
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [includeArchived, setIncludeArchived] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setFromDate('');
      setToDate('');
      setIncludeArchived(false);
      setPreview(null);
      setError('');
    }
  }, [open]);

  // Fetch preview count when dates or archived toggle change
  const fetchPreview = useCallback(async () => {
    if (!fromDate || !toDate) {
      setPreview(null);
      return;
    }
    if (fromDate > toDate) {
      setPreview(null);
      setError('"From" date must be before "To" date.');
      return;
    }

    setPreviewLoading(true);
    setError('');
    try {
      let query = supabase
        .from('uploads')
        .select('id, total_amount')
        .in('status', ['processed', 'completed'])
        .gte('work_week_from_date', fromDate)
        .lte('work_week_to_date', toDate);

      if (!includeArchived) {
        query = query.eq('is_archived', false);
      }

      const { data, error: queryError } = await query;
      if (queryError) throw queryError;

      const results = data || [];
      const total = results.reduce((sum, u) => sum + (Number(u.total_amount) || 0), 0);
      setPreview({ count: results.length, total });
    } catch (err) {
      setError('Failed to fetch preview: ' + err.message);
      setPreview(null);
    } finally {
      setPreviewLoading(false);
    }
  }, [fromDate, toDate, includeArchived]);

  useEffect(() => {
    if (open && fromDate && toDate) {
      const timeout = setTimeout(fetchPreview, 300);
      return () => clearTimeout(timeout);
    }
  }, [open, fromDate, toDate, includeArchived, fetchPreview]);

  const handleExport = async () => {
    setExporting(true);
    setError('');
    try {
      const bulkData = await fetchBulkExportData(supabase, fromDate, toDate, { includeArchived });

      if (bulkData.locationRows.length === 0) {
        setError('No location data found for the selected date range.');
        return;
      }

      const csvContent = generateBulkCSV(bulkData);
      const filename = `bulk-export-${fromDate}-to-${toDate}.csv`;
      downloadFile(csvContent, filename);
      onClose();
    } catch (err) {
      setError('Export failed: ' + err.message);
    } finally {
      setExporting(false);
    }
  };

  const canExport = fromDate && toDate && fromDate <= toDate && preview && preview.count > 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Bulk Export</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Export all payment data across multiple uploads into a single CSV file.
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <TextField
            label="From Date"
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
          <TextField
            label="To Date"
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
        </Box>

        <FormControlLabel
          control={
            <Checkbox
              checked={includeArchived}
              onChange={(e) => setIncludeArchived(e.target.checked)}
            />
          }
          label="Include archived uploads"
        />

        {previewLoading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
            <CircularProgress size={16} />
            <Typography variant="body2">Checking...</Typography>
          </Box>
        )}

        {preview && !previewLoading && (
          <Typography variant="body2" sx={{ mt: 1, fontWeight: 500 }}>
            Found {preview.count} upload{preview.count !== 1 ? 's' : ''} totaling ${preview.total.toFixed(2)}
          </Typography>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          startIcon={exporting ? <CircularProgress size={18} color="inherit" /> : <GetAppIcon />}
          onClick={handleExport}
          disabled={!canExport || exporting}
        >
          {exporting ? 'Exporting...' : 'Export CSV'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BulkExportDialog;
